import React from 'react';
import { Link } from 'react-router-dom';
import { CheckIcon, MinusIcon, PlusIcon } from 'lucide-react';
import { includedGear, gearColumns, media, GearItem, GearStatus } from '../data/product';
import { requestOffer } from '../utils/offerBus';
import { useTrackOnView } from '../hooks/useTrackOnView';
import { useQuickBuild, quickAddPrices } from '../hooks/useQuickBuild';
import { QuickBuildPanel } from './QuickBuildPanel';
import { money } from './QuantityStepper';
import type { QuickAddId } from '../data/product';
const quickAddLabels: Record<QuickAddId, string> = {
  fullLens: 'Add the other 2 lenses',
  hardCase: 'Add hard case',
  careKit: 'Add care kit',
  rx: 'Add RX insert',
  lifetime: 'Add Lifetime protection'
};
type ColumnKey = (typeof gearColumns)[number]['key'];

/**
 * What ships with each way to buy, side by side: the $29.95 Essential only
 * includes what's listed for it; everything else is an add-on or comes with
 * the Complete System and multi-packs.
 */
function goToColumn(key: ColumnKey, event: React.MouseEvent<HTMLAnchorElement>) {
  if (key === 'essential') return; // plain #build-your-x1 anchor
  event.preventDefault();
  requestOffer(key === 'complete' ? 'single' : 'trio');
}
function StatusCell({
  status,
  column
}: {
  status: GearStatus;
  column: string;
}) {
  const Icon = status.kind === 'included' ? CheckIcon : status.kind === 'addon' ? PlusIcon : MinusIcon;
  const tone = status.kind === 'included' ? 'text-offwhite' : status.kind === 'addon' ? 'text-grit-400' : 'text-steel-500';
  const iconTone = status.kind === 'included' ? 'text-grit-500' : status.kind === 'addon' ? 'text-grit-400' : 'text-steel-600';
  return <div className="bg-ink-950 px-3 py-2.5 lg:bg-transparent lg:px-0 lg:py-0" data-id="element-642" data-mp="2d15xlqddb5ch">
      <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-steel-500 lg:hidden" data-id="element-643" data-mp="nmrdjjdao6nb">{column}</p>
      <p className={`mt-1 flex items-start gap-1.5 text-[13px] font-semibold leading-snug lg:mt-0 ${tone}`} data-id="element-644" data-mp="inwhvgu8anls">
        <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${iconTone}`} aria-hidden="true" data-id="element-645" data-mp="2iqfjsxlrr7d2" />
        {status.label}
      </p>
    </div>;
}
const PROTECTION_BADGE = 'https://cdn.magicpatterns.com/uploads/fgn15VRLFuUHNXjK51LK5X/0DA33D70-743E-4305-91DE-D0EDB3114662.png';
function GearThumb({
  item
}: {
  item: GearItem;
}) {
  if (item.image) {
    return <div className="aspect-[4/3] w-full overflow-hidden border border-white/10 bg-ink-900" data-id="element-646" data-mp="2wr06i4ut1nbu">
        <img src={item.image} alt={item.name} className="h-full w-full object-cover" loading="lazy" data-id="element-647" data-mp="31oi44z3dgg4j" />
      </div>;
  }
  return <div className="aspect-[4/3] w-full overflow-hidden border border-[#f2c94c]/40 bg-black" data-id="element-648" data-mp="3bel6hpwdv6fj">
      <img src={PROTECTION_BADGE} alt="GritVue Lifetime Protection breakage coverage badge" className="h-full w-full object-contain" loading="lazy" data-id="element-649" data-mp="3bgxou461zit4" />
    </div>;
}
export function IncludedGear() {
  const ref = useTrackOnView<HTMLElement>('mystery_gift_offer_viewed');
  const build = useQuickBuild();
  return <section ref={ref} aria-labelledby="included-gear-heading" className="mt-10 border border-white/12 bg-ink-950 lg:mt-12" data-id="element-650" data-mp="1drsh1nspe2c2">
      <div className="grid gap-6 border-b border-white/10 px-5 py-6 sm:px-7 lg:grid-cols-[1fr_380px] lg:items-center" data-id="element-651" data-mp="18qlatt67lb7i">
        <div data-id="element-652" data-mp="zr7won753ygg">
          <h3 id="included-gear-heading" className="grit-headline text-[clamp(1.5rem,4vw,2.5rem)] font-extrabold uppercase text-offwhite" data-id="element-653" data-mp="zrhp0hfnqhpb">
            What&apos;s in the box? <span className="text-grit-500" data-id="element-654" data-mp="1sg5hawytq7cd">You choose.</span>
          </h3>
          <p className="mt-2 max-w-2xl text-[15px] leading-relaxed text-steel-400" data-id="element-655" data-mp="1si9gx2powk6u">
            The X1 Essential ships with the X1, one lens of your choice, a USB-C cable and the Quick Start Guide, plus
            only the add-ons you pick. The Complete System comes with everything below. Every X1 in a multi-pack is a
            Complete System.
          </p>
        </div>
        <figure className="border border-white/10 bg-ink-900" data-id="element-656" data-mp="2bdgd27l8yin2">
          {/* Cropped on the right so the anti-fog spray (sold separately) stays out of frame. */}
          <div className="w-full overflow-hidden bg-black" style={{
          aspectRatio: '1105 / 1254'
        }} data-id="element-657" data-mp="1zylfq54mje75">
            <img src="https://cdn.magicpatterns.com/uploads/hPkSqjWLBFxj6o9jAzPCn2/D3D967EB-F303-4189-842F-BCB4DB15CDF8.png" alt="The GritVue X1 Complete System: X1 glasses, Clear, Amber and Smoke lenses, hard case, pouch and cleaning cloth" className="block h-auto w-[113.5%] max-w-none" loading="lazy" data-id="element-658" data-mp="1lzc31ffmn27x" />
          </div>
          <figcaption className="border-t border-white/10 px-3 py-2 text-[11px] font-bold uppercase tracking-[0.14em] text-steel-400" data-id="element-659" data-mp="24xy3cs3bkpas">
            The Complete System, organized in its hard case
          </figcaption>
        </figure>
      </div>

      {/* Column headers (desktop) */}
      <div className="hidden grid-cols-[160px_minmax(0,1fr)_repeat(3,170px)] items-end gap-5 border-b border-white/10 px-7 py-4 lg:grid" data-id="element-660" data-mp="32ck1x023w1xi">
        <span data-id="element-661" data-mp="3bqb06rg5f3qc" />
        <span data-id="element-662" data-mp="3bq2jspkthgae" />
        {gearColumns.map(column => <a key={column.key} href={column.key === 'essential' ? '#build-your-x1' : '#offer'} onClick={event => goToColumn(column.key, event)} className="group block focus:outline-none focus-visible:ring-2 focus-visible:ring-grit-500" data-id="element-663" data-mp="37qk3y87urvef">
            <span className="block font-display text-lg font-extrabold uppercase leading-none text-offwhite transition-colors duration-150 ease-grit group-hover:text-grit-500" data-id="element-664" data-mp="2ss86hzcnoim7">
              {column.title}
            </span>
            <span className="mt-1 block text-[12px] font-bold uppercase tracking-[0.14em] text-grit-500" data-id="element-665" data-mp="32rqoia909ubm">
              {column.price}
            </span>
          </a>)}
      </div>

      <ul className="divide-y divide-white/10" data-id="element-666" data-mp="hznhpx8x5cfl">
        {includedGear.map(item => <li key={item.name} className="grid gap-4 px-5 py-5 sm:grid-cols-[140px_minmax(0,1fr)] sm:px-7 lg:grid-cols-[160px_minmax(0,1fr)_repeat(3,170px)] lg:items-center lg:gap-5" data-id="element-667" data-mp="d2exlr47mybp">
            <div className="relative" data-id="element-668" data-mp="p2llaokeada4">
              <GearThumb item={item} data-id="element-669" data-mp="p5rbk74rfl7f" />
              {item.quickAdd && build.isAdded(item.quickAdd) && <span className="absolute left-2 top-2 inline-flex items-center gap-1 bg-grit-500 px-2 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-ink-950" data-id="element-670" data-mp="z22pt2nwul4i">
                  <CheckIcon className="h-3 w-3" aria-hidden="true" data-id="element-671" data-mp="1g5d5lw7pa0h" />
                  Added
                </span>}
            </div>
            <div data-id="element-672" data-mp="bbzzos3k3p8j">
              <p className="font-display text-xl font-bold uppercase leading-tight tracking-wide text-offwhite" data-id="element-673" data-mp="bbgt27jcwn9t">
                {item.name}
              </p>
              <p className="mt-1 text-[14px] leading-relaxed text-steel-300" data-id="element-674" data-mp="3et8f75atad03">{item.note}</p>
              {item.quickAdd && (() => {
            const id = item.quickAdd;
            const added = build.isAdded(id);
            return <button type="button" aria-pressed={added} onClick={() => build.toggle(id)} className={`mt-3 inline-flex items-center gap-2 border-2 border-grit-500 px-3.5 py-2 font-display text-[15px] font-extrabold uppercase leading-none tracking-wide transition-[background-color,color,transform] duration-150 ease-grit active:translate-y-[1px] focus:outline-none focus-visible:ring-2 focus-visible:ring-grit-500 ${added ? 'bg-grit-500 text-ink-950 hover:bg-grit-400' : 'text-grit-500 hover:bg-grit-500 hover:text-ink-950'}`} data-id="element-675" data-mp="3c7hkzk6x1prh">
                    {added ? <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-676" data-mp="2em3dsknolakl" /> : <PlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-677" data-mp="2ej7hf0rhety9" />}
                    {added ? 'Added · Remove' : `${quickAddLabels[id]} · +${money(quickAddPrices[id])}`}
                  </button>;
          })()}
              {item.termsLink && <Link to="/legal/guarantee" className="mt-1.5 inline-block text-[12px] text-steel-400 underline underline-offset-2 hover:text-offwhite" data-id="element-678" data-mp="3ththw4ipsor0">
                  See warranty &amp; protection terms
                </Link>}
            </div>
            <div className="grid grid-cols-3 gap-px bg-white/10 sm:col-span-2 lg:contents" data-id="element-679" data-mp="3ok1ot76c2wl8">
              {gearColumns.map(column => <StatusCell key={column.key} status={item[column.key]} column={column.title} data-id="element-680" data-mp="3fhir11hena5u" />)}
            </div>
          </li>)}
      </ul>

      {/* Column shortcuts (mobile / tablet) */}
      <div className="grid grid-cols-3 gap-px border-t border-white/10 bg-white/10 lg:hidden" data-id="element-681" data-mp="35siqver628q2">
        {gearColumns.map(column => <a key={column.key} href={column.key === 'essential' ? '#build-your-x1' : '#offer'} onClick={event => goToColumn(column.key, event)} className="bg-ink-900 px-3 py-3 transition-colors duration-150 ease-grit hover:bg-ink-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-grit-500" data-id="element-682" data-mp="2rtstrz4ck91p">
            <span className="block font-display text-[15px] font-extrabold uppercase leading-tight text-offwhite" data-id="element-683" data-mp="2m6qjlkpjvb80">
              {column.title}
            </span>
            <span className="mt-0.5 block text-[11px] font-bold uppercase tracking-[0.12em] text-grit-500" data-id="element-684" data-mp="2w3lk295dvlox">
              {column.price}
            </span>
          </a>)}
      </div>

      <QuickBuildPanel build={build} data-id="element-685" data-mp="34w5lfhd284dg" />
    </section>;
}